/**
 * Tentpole Calendar — frontend mirrors of backend/src/types/tentpoleCalendar.ts.
 * Field names match the server exactly (tentpoleSlug / creatorSlug / creatorLabel).
 */

export type CalendarEntryStatus = 'submitted' | 'approved' | 'declined' | 'withdrawn';

export const ENTRY_STATUSES: CalendarEntryStatus[] = ['submitted', 'approved', 'declined', 'withdrawn'];

export const ENTRY_STATUS_LABELS: Record<CalendarEntryStatus, string> = {
  submitted: 'Needs review',
  approved: 'Approved',
  declined: 'Declined',
  withdrawn: 'Withdrawn',
};

/**
 * Admin-facing next statuses — the server transition map MINUS the
 * creator-only → submitted arc (the backend rejects admin moves to
 * 'submitted'; entries return there only via creator edits).
 */
export const ENTRY_STATUS_NEXT: Record<CalendarEntryStatus, CalendarEntryStatus[]> = {
  submitted: ['approved', 'declined'],
  approved: ['declined', 'withdrawn'],
  declined: ['approved'],
  withdrawn: [],
};

export type CalendarContentType = 'video' | 'short' | 'livestream' | 'series' | 'irl_event' | 'other';

export const CONTENT_TYPE_LABELS: Record<CalendarContentType, string> = {
  video: 'Video',
  short: 'Short',
  livestream: 'Livestream',
  series: 'Series',
  irl_event: 'IRL event',
  other: 'Other',
};

/** How often the creator expects this plan to repeat. */
export type CalendarRecurrence =
  | 'one_time'
  | 'daily'
  | 'weekly'
  | 'biweekly'
  | 'monthly'
  | 'quarterly'
  | 'unknown';

export const CALENDAR_RECURRENCES: CalendarRecurrence[] = [
  'one_time',
  'daily',
  'weekly',
  'biweekly',
  'monthly',
  'quarterly',
  'unknown',
];

export const RECURRENCE_LABELS: Record<CalendarRecurrence, string> = {
  one_time: 'One-Time',
  daily: 'Daily',
  weekly: 'Weekly',
  biweekly: 'Biweekly',
  monthly: 'Monthly',
  quarterly: 'Quarterly',
  unknown: 'Unknown',
};

/**
 * The creator's confidence/lifecycle for the plan. This is deliberately
 * separate from CalendarEntryStatus, which remains the CG approval workflow.
 */
export type CalendarPlanStatus =
  | 'confirmed'
  | 'likely'
  | 'in_discussion'
  | 'exploratory_idea'
  | 'paused'
  | 'cancelled'
  | 'unknown';

export const CALENDAR_PLAN_STATUSES: CalendarPlanStatus[] = [
  'confirmed',
  'likely',
  'in_discussion',
  'exploratory_idea',
  'paused',
  'cancelled',
  'unknown',
];

export const PLAN_STATUS_LABELS: Record<CalendarPlanStatus, string> = {
  confirmed: 'Confirmed',
  likely: 'Likely',
  in_discussion: 'In Discussion',
  exploratory_idea: 'Exploratory Idea',
  paused: 'Paused',
  cancelled: 'Cancelled',
  unknown: 'Unknown',
};

export type CalendarDatePrecision = 'exact' | 'week' | 'month';

/** Persisted targeting mode returned by the v2 calendar API. */
export type CalendarScopeMode = 'selected' | 'all_page_snapshot';
export type CalendarMigrationState = 'legacy_all_requires_review' | 'quarantined';

/** Request shape used by admin create/update routes. */
export type CalendarScopeInput =
  | { mode: 'all' }
  | { mode: 'selected'; channelIds: string[] };

export const PRECISION_LABELS: Record<CalendarDatePrecision, string> = {
  exact: 'Exact day',
  week: 'That week',
  month: 'That month',
};

export type TentpoleCategory =
  | 'holiday'
  | 'sports'
  | 'music'
  | 'awards'
  | 'shopping'
  | 'culture'
  | 'gaming'
  | 'tech'
  | 'film'
  | 'automotive'
  | 'heritage'
  | 'seasonal'
  | 'other';

export const TENTPOLE_CATEGORIES: TentpoleCategory[] = [
  'holiday',
  'sports',
  'music',
  'awards',
  'shopping',
  'culture',
  'gaming',
  'tech',
  'film',
  'automotive',
  'heritage',
  'seasonal',
  'other',
];

export const TENTPOLE_CATEGORY_LABELS: Record<TentpoleCategory, string> = {
  holiday: 'Holiday',
  sports: 'Sports',
  music: 'Music',
  awards: 'Awards',
  shopping: 'Shopping',
  culture: 'Culture',
  gaming: 'Gaming',
  tech: 'Tech & business',
  film: 'Film & TV',
  automotive: 'Automotive',
  heritage: 'Heritage',
  seasonal: 'Seasonal',
  other: 'Other',
};

export interface Tentpole {
  slug: string; // doc id, frozen at creation
  name: string;
  emoji?: string;
  description?: string; // internal curation notes
  startDate: string; // YYYY-MM-DD
  endDate: string; // YYYY-MM-DD
  category: TentpoleCategory;
  active: boolean;
  externalVisible: boolean; // reserved for the future external calendar
  externalDescription?: string;
  createdAt: string;
  createdBy: string;
  updatedAt: string;
  updatedBy?: string;
}

export interface TentpoleInput {
  slug?: string;
  name: string;
  emoji?: string;
  description?: string;
  startDate: string;
  endDate: string;
  category: TentpoleCategory;
  active?: boolean;
  externalVisible?: boolean;
  externalDescription?: string;
}

export interface CalendarEntryStatusChange {
  from: CalendarEntryStatus;
  to: CalendarEntryStatus;
  at: string;
  byUid: string;
  byEmail: string;
  note?: string;
}

/** Denormalized targeting snapshot stored with each entry. */
export interface CalendarTargetChannel {
  channelId: string;
  channelLabel: string;
}

export type CalendarEmailSendState = 'pending' | 'sent' | 'failed' | 'skipped';

export interface CalendarEntry {
  id: string; // client-minted UUID
  /** Exactly one of tentpoleSlug|customLabel is set. */
  tentpoleSlug?: string;
  /** Free-form moment name (1–80 chars) for entries not on the tentpole list. */
  customLabel?: string;
  creatorSlug: string; // creatorReportPages slug
  creatorLabel: string; // clientLabel snapshot
  /**
   * V2 channel scope. `all_page_snapshot` captures the page roster at write
   * time; `selected` targets the explicit IDs below.
   */
  scopeMode?: CalendarScopeMode;
  /** Manager detail may expose the reason; list rows expose only the boolean. */
  migrationState?: CalendarMigrationState;
  /** Sanitized list signal that approval needs an explicit manager decision. */
  requiresManagerReview?: boolean;
  targetChannelIds?: string[];
  targetChannels?: CalendarTargetChannel[];
  /** Legacy v1 attribution retained while old entries are migrated. */
  channelId?: string;
  channelLabel?: string; // CRM display name snapshot at write time
  title: string;
  contentType: CalendarContentType;
  plannedDate: string; // start; datePrecision applies here
  plannedEndDate?: string; // YYYY-MM-DD, >= plannedDate; absent = single-day
  datePrecision: CalendarDatePrecision;
  /** Optional while legacy rows roll forward; renderers normalize to Unknown. */
  recurrence?: CalendarRecurrence;
  /** Creator-owned plan lifecycle, distinct from the approval `status`. */
  planStatus?: CalendarPlanStatus;
  /** Advertiser-safe, creator-approved summary. */
  advertiserSummary?: string;
  /** Legacy v1 alias; readers should prefer advertiserSummary. */
  externalDescription?: string;
  /** Creator-authored confidential context, manager detail responses only. */
  creatorPrivateNote?: string;
  /** CG-authored staff note, manager detail responses only. */
  internalNote?: string;
  status: CalendarEntryStatus;
  /** Sanitized list responses may omit audit/confidential fields below. */
  source?: 'creator' | 'admin';
  /** Set when the entry was added through the Claude connector (source stays 'admin'). */
  createdVia?: 'mcp';
  statusHistory?: CalendarEntryStatusChange[];
  emailStatus?: { internal: CalendarEmailSendState };
  meta?: { ip?: string; userAgent?: string };
  eventReportId?: string; // reserved
  createdAt: string;
  updatedAt: string;
  createdBy?: string;
}

export interface EntryFilters {
  tentpoleSlug?: string;
  status?: CalendarEntryStatus;
  creatorSlug?: string;
  source?: 'creator' | 'admin';
  channelId?: string;
  /** true = only custom-moment entries (customLabel set). */
  customOnly?: boolean;
  plannedFrom?: string;
  plannedTo?: string;
  limit?: number;
  cursor?: string;
}

export interface AdminEntryInput {
  entryId: string;
  creatorSlug: string;
  /** Exactly one of tentpoleSlug|customLabel. On PATCH, sending one clears the other. */
  tentpoleSlug?: string;
  customLabel?: string;
  /** V2 request scope; sending mode=all snapshots the page's current roster. */
  scope?: CalendarScopeInput;
  /** Legacy v1 attribution accepted during the backend transition. */
  channelId?: string;
  title: string;
  contentType: CalendarContentType;
  plannedDate: string;
  /** '' clears on PATCH (back to single-day). */
  plannedEndDate?: string;
  datePrecision: CalendarDatePrecision;
  recurrence?: CalendarRecurrence;
  planStatus?: CalendarPlanStatus;
  advertiserSummary?: string;
  internalNote?: string;
  status?: 'submitted' | 'approved';
}

/** Admin PATCH body — any entry field except identity and status. */
export type AdminEntryPatch = Partial<Omit<AdminEntryInput, 'entryId' | 'creatorSlug' | 'status'>>;

export interface CalendarPageChannel {
  channelId: string;
  channelName: string;
  /** Tolerated v2 alias if the page picker adopts the target snapshot name. */
  channelLabel?: string;
}

/** Slim creator-page row for the EntryEditor pickers (GET /creator-pages). */
export interface CalendarPageOption {
  slug: string;
  clientLabel: string;
  pageType: 'creator' | 'agency';
  revoked?: boolean;
  /** CRM-resolved channel refs — read defensively as (p.channels ?? []). */
  channels: CalendarPageChannel[];
}
